import {
	type BackendPlacementMessage,
	type FrontendPlacementMessage,
	type FrontendStage,
	noValidation,
} from '@post-office/placement-common-v2';
import {
	PlacementBackend,
	PlacementFrontend,
	type PlacementRenderProps,
} from '@post-office/placements--growth/nav-4-projects-section';

import { Nav4Recommendation } from '../../../components/nav-4-recommendation';
import {
	IT_SERVICE_MANAGEMENT,
	SIMPLIFIED_IT_SERVICE_MANAGEMENT_ADVANCED,
	SIMPLIFIED_IT_SERVICE_MANAGEMENT_BASIC,
	type MessageTemplateContext,
} from '../../../index';
import { ProductKey } from '@post-office/tcs-client';
import type { LicenseInformation } from '@post-office/message-template-contracts/clients/tcs';
import {
	JiraProductDiscoveryCcpId,
	JiraServiceManagementCcpId,
	type TransformMetadataResult,
	ValidEdition,
	type ValidProductCcpId,
} from '@post-office/shared-contracts/message-template-metadata';
import { Targets } from '@atlassiansox/cross-flow-base-types';
import {
	type CreatePlacementMessageConfig,
	type BackendSyncMessageStage,
	type AnyPayload,
	type AnyBackendMessageStage,
} from '@post-office/placement-common-v2/create-placement/types';

const hydrate = PlacementBackend.createBackendStage({
	type: 'hydrate',
	validate: noValidation<MessageTemplateContext>(),
	async stage({ message, request }): Promise<MessageTemplateContext> {
		const {
			context: { entityId },
		} = message;

		if (entityId !== IT_SERVICE_MANAGEMENT) {
			return message.context;
		}

		try {
			const tcsResponse =
				await request.restApiClients.tcsClient.cloud.getTcsRecord<LicenseInformation>(
					`${request.orchestrationContext.tenantId}.xflow.license.information`,
				);

			const isPremium = ['premium', 'business'].includes(
				tcsResponse.data.products[ProductKey.JIRA_SOFTWARE]?.edition ?? '',
			);

			return {
				...message.context,
				entityId: isPremium
					? SIMPLIFIED_IT_SERVICE_MANAGEMENT_ADVANCED
					: SIMPLIFIED_IT_SERVICE_MANAGEMENT_BASIC,
			};
		} catch (error) {
			request.logger.error(
				{ error },
				`Error occurred when retrieving license information from TCS`,
			);
			throw new Error('Something went wrong fetching license information from TCS');
		}
	},
});

const transformMetadata = PlacementBackend.createBackendStage({
	type: 'transformMetadata',
	validate: noValidation<MessageTemplateContext>(),
	stage({ message }) {
		const targetProduct: ValidProductCcpId[] =
			message.context.productKey === Targets.JIRA_SERVICE_DESK
				? [JiraServiceManagementCcpId]
				: [JiraProductDiscoveryCcpId];
		return {
			goalMetadata: {
				primaryGoal: {
					goalId: 'd1to6ai',
					targetProduct,
					edition: [ValidEdition.AnyEdition],
				},
			},
		};
	},
});

export const MessageBackend: (config: CreatePlacementMessageConfig) => BackendPlacementMessage<{
	transformMetadata: BackendSyncMessageStage<AnyPayload, TransformMetadataResult>;
	hydrate?: AnyBackendMessageStage;
}> = PlacementBackend.createMessageBackend(() => ({
	transformMetadata,
	hydrate,
}));

type RenderProps = MessageTemplateContext & PlacementRenderProps;

const render = PlacementFrontend.createFrontendStage({
	type: 'render',
	validate: noValidation<RenderProps>(),
	Component: (props: RenderProps) => <Nav4Recommendation {...props} />,
});

export const MessageFrontend: (config: CreatePlacementMessageConfig) => FrontendPlacementMessage<{
	render: FrontendStage<PlacementRenderProps>;
}> = PlacementFrontend.createMessageFrontend({
	render,
});
